import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const ANNOUNCEMENT_ICONS = [
  'Megaphone',
  'Sparkles',
  'Video',
  'Radio',
  'Rocket',
  'Bell',
  'Zap',
  'Calendar',
  'PlayCircle',
] as const;
export type AnnouncementIcon = typeof ANNOUNCEMENT_ICONS[number];

export interface AnnouncementBannerConfig {
  enabled: boolean;
  icon: AnnouncementIcon;
  text: string;
  cta_label: string;
  cta_url: string;
}

const DEFAULT: AnnouncementBannerConfig = {
  enabled: false,
  icon: 'Megaphone',
  text: '',
  cta_label: '',
  cta_url: '',
};

const KEY = 'announcement_banner';

export function useAnnouncementBanner() {
  return useQuery({
    queryKey: ['platform_settings', KEY],
    queryFn: async (): Promise<AnnouncementBannerConfig> => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', KEY)
        .maybeSingle();
      if (error) throw error;
      return { ...DEFAULT, ...((data?.value as Partial<AnnouncementBannerConfig>) ?? {}) };
    },
    staleTime: 60_000,
  });
}

export function useUpdateAnnouncementBanner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: AnnouncementBannerConfig) => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: KEY, value: config as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', KEY] });
      toast.success('Anuncio actualizado');
    },
    onError: (e: any) => toast.error(e.message ?? 'Error al actualizar'),
  });
}
